import { useEffect, useState } from 'react';

import { useUserProfile } from '@atom/hooks/useUserProfile';
import { Modal, Snackbar, TextField } from '@atom/mui';
import api from '@atom/utilities/api';
import { WORK_ORDERS_ENDPOINT } from '@atom/utilities/endpoints';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

interface Props {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  workOrderId: string;
  taskId: string;
  refetch: () => Promise<any>;
}

const styles = {
  modal: {
    padding: '1.5rem',
  },
};

const RejectModal = ({
  open,
  onClose,
  loading,
  setLoading,
  workOrderId,
  taskId,
  refetch,
}: Props) => {
  const userProfile = useUserProfile();

  const [reason, setReason] = useState<string>('');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    setReason('');
    setError('');
  }, [open]);

  useEffect(() => {
    if (isNilOrEmpty(reason)) {
      setError('You must provide a reason to reject this task');
    } else {
      setError('');
    }
  }, [reason]);

  const handleSubmit = async () => {
    try {
      setLoading(true);

      await api.post(
        `${WORK_ORDERS_ENDPOINT}/${workOrderId}/tasks/${taskId}/reject`,
        {
          message: reason,
          userId: userProfile.userId,
        },
      );

      setLoading(false);

      Snackbar.info({ message: 'Task rejected' });

      onClose();
      refetch();
    } catch (err) {
      setLoading(false);
      onClose();
      Snackbar.error({ message: 'Failed to reject task' });
    }
  };

  return (
    <Modal
      open={open}
      title="Reject Task"
      onCancel={onClose}
      onConfirm={handleSubmit}
      confirmButtonText="Send"
      onExited={onClose}
      loading={loading}
      contentStyle={styles.modal}
      disabled={isNilOrEmpty(reason)}
    >
      <TextField
        multiline
        label="Reason for Rejection"
        name="reason"
        value={reason}
        error={!isNilOrEmpty(error)}
        helperText={error}
        onChange={event => {
          setReason(event.target.value);
        }}
      />
    </Modal>
  );
};

export default RejectModal;
